import * as THREE from "three";
import type { HeightFn } from "./groundFollow";
import { FOOT_IK, attachLegs, type Leg } from "./footLeg";
import { placeLeg } from "./footPlace";
import { holdOf, pinLeg, trackStep } from "./footPin";

export { FOOT_IK } from "./footLeg";

/**
 * The foot IK (the rules and tunables: footLeg.ts): each frame, after the clip has posed a unit and groundFollow has
 * placed it, the soles are measured, the pelvis eased so the planted ones can reach the ground, the lock's pins held
 * (footPin.ts) and each leg placed (footPlace.ts). Only units on uneven ground or locking run it.
 */

const _inv = new THREE.Matrix4();
const _s = new THREE.Vector3(), _h = new THREE.Vector3(), _a = new THREE.Vector3(), _sc = new THREE.Vector3();

export class FootIk {
  /** The pelvis offset (world units, added to the body's height) and its rate. */
  pelvis = 0;
  private pv = 0;
  private placed = false;
  /** The horizontal lock (`lock`): the weight asked for and the eased weight in use. */
  private lockGoal = 0;
  private lockW = 0;
  private held = 0;

  private constructor(private readonly legs: readonly Leg[], private readonly longest: number) {}

  /**
   * The legs of `model` under `body` (its `boot_l/_r` with a `shin`, and `arm_l/_r` only when they carry a `fore`),
   * each with an `#ik` group slipped in above its parts; null for a rig without legs. Run on the rest pose.
   */
  static attach(body: THREE.Object3D, model: string): FootIk | null {
    const legs = attachLegs(body, model);
    if (legs.length === 0) return null;
    return new FootIk(legs, Math.max(...legs.map((l) => (l.lower ? l.l1 + l.l2 : l.len))));
  }

  /** The legs back as the clip poses them, the pelvis level, no lock (a death, a new spawn). */
  reset(): void {
    this.clearGroups();
    this.pelvis = this.pv = 0;
    this.placed = false;
    this.lockGoal = this.lockW = 0;
    for (const l of this.legs) { l.pinned = false; l.low = l.high = 0; }
  }

  /**
   * Holds planted soles where they are across the ground, `weight` 0–1 (the walk asks for it while a unit turns on the
   * spot, settles to a stop, or fades back into its walk; enemyFeel.ts). While the weight is above 0, a sole the clip
   * holds down is pinned at the x/z it has when it plants, and let go when the clip lifts it or carries it too far
   * (footPin.ts: `releaseShare`, `releaseAway`); the pin is reached by turning and stretching the leg about its hip
   * (two parts: the knee). The pinned sole also stays on the ground under it. A weight falling to 0 eases the soles
   * back to the clip. Call each frame before `solve`.
   */
  lock(weight: number): void { this.lockGoal = Math.max(0, Math.min(1, weight)); }

  /** The lock's weight in use (eased), for the walk's own blend and the tests. */
  get locked(): number { return this.lockW; }

  /**
   * Places the feet of `body` (posed by its clip and placed by groundFollow) on `ground`. `uneven`: the ground under the
   * footprint is uneven enough to matter (groundFollow.ts GroundTrack.relief); on even ground the legs ease back to the
   * clip's pose. Call after the body's pose and position for the frame are set.
   */
  solve(body: THREE.Object3D, ground: HeightFn, uneven: boolean, dt: number): void {
    this.held = this.lockGoal > 0 ? 0 : this.held + dt;
    if (this.lockGoal > 0) this.lockW = 1;
    else if (this.held > FOOT_IK.unlockHold) this.lockW *= Math.exp(-FOOT_IK.lockRate * dt);
    if (this.lockGoal === 0 && this.lockW < 1e-3) { this.lockW = 0; for (const l of this.legs) l.pinned = false; }
    const locking = this.lockW > 0;
    if (!uneven && !locking && Math.abs(this.pelvis) < 1e-4) {
      if (this.placed) { this.clearGroups(); this.placed = false; }
      this.pelvis = this.pv = 0;
      return;
    }
    if (this.placed) this.clearGroups();
    body.updateWorldMatrix(true, true);                         // its parent too: the root has moved this frame
    _inv.copy(body.matrixWorld).invert();
    const sy = _sc.setFromMatrixScale(body.matrixWorld).y;
    let lo = -Infinity, hi = Infinity, sum = 0, wsum = 0;
    for (const l of this.legs) {
      const last = l.lower ?? l.upper;
      _s.copy(l.sole).applyMatrix4(last.matrixWorld);
      l.lift = Math.max(0, _a.copy(_s).applyMatrix4(_inv).y * sy);
      const reach = (l.lower ? l.l1 + l.l2 : l.len) * sy;
      l.weight = 1 / (1 + (l.lift / (FOOT_IK.plantedBelow * reach)) ** 2);
      trackStep(l, reach, dt);
      pinLeg(l, _s.x, _s.z, reach, this.lockGoal > 0 && !l.sprawl);
      const w = l.pinned ? this.lockW * holdOf(l, _s.x, _s.z, reach) : 0;
      l.tx = _s.x + (l.px - _s.x) * w;
      l.tz = _s.z + (l.pz - _s.z) * w;
      l.need = uneven || l.pinned ? ground(l.tx, l.tz) + l.lift - _s.y : 0;
      l.free = l.sprawl && Math.abs(l.need) < FOOT_IK.sprawlSlack * reach;   // a sprawler on gentle ground keeps its clip pose
      if (l.free) l.need = 0;
      if (l.sprawl) l.weight = 0;
      if (l.lower) {
        _h.setFromMatrixPosition(l.upper.matrixWorld);
        const reach = (l.l1 + l.l2) * sy, d = _h.distanceTo(_s);
        l.shortenBy = Math.max(0, d - FOOT_IK.reachMin * reach);
        l.lengthenBy = Math.max(0, FOOT_IK.reachMax * reach - d);
      } else {
        l.shortenBy = (l.weight > 0.5 ? FOOT_IK.shorten : FOOT_IK.tuck) * l.len * sy;
        l.lengthenBy = FOOT_IK.lengthen * l.len * sy;
      }
      sum += l.weight * l.need; wsum += l.weight;
      lo = Math.max(lo, l.need - l.shortenBy);                  // no sole further into the bank than its leg can shorten
      if (l.weight > 0.5) hi = Math.min(hi, l.need + l.lengthenBy);
    }
    const cap = FOOT_IK.pelvisMax * this.longest * sy;
    let want = wsum > 0 ? sum / wsum : 0;
    want = Math.max(lo, Math.min(hi, want));
    if (lo > hi) want = lo;
    want = Math.max(-cap, Math.min(cap, want));
    this.easePelvis(want, dt);
    body.position.y += this.pelvis;
    for (const l of this.legs) if (!l.free) placeLeg(l, l.need - this.pelvis);
    this.placed = true;
  }

  /** How many legs it places, and where leg `i`'s sole is in the world as last drawn (for the tests and captures). */
  get count(): number { return this.legs.length; }
  sole(i: number, out: THREE.Vector3): THREE.Vector3 {
    const l = this.legs[i];
    return out.copy(l.sole).applyMatrix4((l.lower ?? l.upper).matrixWorld);
  }

  private easePelvis(goal: number, dt: number): void {
    const w = FOOT_IK.pelvisOmega, x0 = this.pelvis - goal, q = this.pv + w * x0, e = Math.exp(-w * dt);
    this.pelvis = goal + (x0 + q * dt) * e;
    this.pv = (this.pv - w * q * dt) * e;
  }

  private clearGroups(): void {
    for (const l of this.legs) { l.g1.position.set(0, 0, 0); l.g1.quaternion.identity(); l.g1.scale.set(1, 1, 1); l.g2?.position.set(0, 0, 0); l.g2?.quaternion.identity(); }
  }
}
